<script lang="ts" setup>
import { ChevronRight } from 'lucide-vue-next';

import { useSidebar } from '@/components/ui/sidebar';

import type { NavGroup, NavItem } from './types';

const { navMain } = defineProps<{
  navMain: NavGroup[];
}>();

const route = useRoute();
const router = useRouter();

const { state, isMobile } = useSidebar();

function isCollapsed(menu: NavItem): boolean {
  const pathname = route.path;
  navMain.forEach(group => {
    group.items.forEach(item => {
      if (item.url === pathname) {
        return true;
      }
    });
  });
  return !!menu.items?.some(item => item.url === pathname);
}

function isActive(menu: NavItem): boolean {
  const pathname = route.path;
  if (menu.url) {
    return pathname === menu.url;
  }
  return !!menu.items?.some(item => item.url === pathname);
}

// Handle navigation with date range query params
function handleNavigate(menu: NavItem) {
  if (!menu.url) return;

  // For /calls and /staff-timeline, preserve date range from current route
  if (menu.url === '/calls' || menu.url === '/staff-timeline') {
    router.push({
      path: menu.url,
      query: {
        startDate: route.query.startDate as string,
        endDate: route.query.endDate as string,
      },
    });
  } else {
    router.push(menu.url);
  }
}
</script>

<template>
  <UiSidebarGroup v-for="group in navMain" :key="group.title">
    <UiSidebarGroupLabel>{{ group.title }}</UiSidebarGroupLabel>
    <UiSidebarMenu>
      <template v-for="menu in group.items" :key="menu.title">
        <UiSidebarMenuItem v-if="!menu.items && !(isMobile && menu.hideOnMobile)">
          <UiSidebarMenuButton
            :is-active="isActive(menu)"
            :tooltip="menu.title"
            @click="handleNavigate(menu)"
          >
            <component :is="menu.icon" />
            <span>{{ menu.title }}</span>
          </UiSidebarMenuButton>
        </UiSidebarMenuItem>

        <UiSidebarMenuItem v-else-if="!(isMobile && menu.hideOnMobile)">
          <!-- sidebar expanded -->
          <UiCollapsible
            v-if="state !== 'collapsed' || isMobile"
            as-child
            :default-open="isCollapsed(menu)"
            class="group/collapsible"
          >
            <UiSidebarMenuItem>
              <UiCollapsibleTrigger as-child>
                <UiSidebarMenuButton :tooltip="menu.title">
                  <component :is="menu.icon" v-if="menu.icon" />
                  <span>{{ menu.title }}</span>
                  <ChevronRight
                    class="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90"
                  />
                </UiSidebarMenuButton>
              </UiCollapsibleTrigger>
            </UiSidebarMenuItem>
            <UiCollapsibleContent>
              <UiSidebarMenuSub>
                <UiSidebarMenuSubItem v-for="subItem in menu.items" :key="subItem.title">
                  <UiSidebarMenuSubButton
                    :is-active="isActive(subItem as NavItem)"
                    @click="handleNavigate(subItem as NavItem)"
                  >
                    <component :is="subItem.icon" v-if="subItem.icon" />
                    <span>{{ subItem.title }}</span>
                  </UiSidebarMenuSubButton>
                </UiSidebarMenuSubItem>
              </UiSidebarMenuSub>
            </UiCollapsibleContent>
          </UiCollapsible>

          <!-- sidebar collapsed -->
          <UiDropdownMenu v-else>
            <UiDropdownMenuTrigger as-child>
              <UiSidebarMenuButton :tooltip="menu.title">
                <component :is="menu.icon" v-if="menu.icon" />
                <span>{{ menu.title }}</span>
              </UiSidebarMenuButton>
            </UiDropdownMenuTrigger>
            <UiDropdownMenuContent align="start" side="right">
              <UiDropdownMenuLabel>{{ menu.title }}</UiDropdownMenuLabel>
              <UiDropdownMenuSeparator />
              <UiDropdownMenuItem
                v-for="subItem in menu.items"
                :key="subItem.title"
                @click="handleNavigate(subItem as NavItem)"
              >
                <component :is="subItem.icon" v-if="subItem.icon" />
                <span>{{ subItem.title }}</span>
              </UiDropdownMenuItem>
            </UiDropdownMenuContent>
          </UiDropdownMenu>
        </UiSidebarMenuItem>
      </template>
    </UiSidebarMenu>
  </UiSidebarGroup>
</template>
